/** Cliente HTTP único contra la API. Guarda el token y traduce los errores. */

import { claveDelAmbito } from "./ambito";
import { conexionCaida, conexionViva } from "./conexion";

/*
 * La aplicación de gestión y el portal del socio son dos sesiones distintas y
 * guardan su token por separado, para que abrir una no cierre la otra. De
 * repartirlas se encarga lib/ambito.
 */
const claveToken = () => claveDelAmbito("token");
const claveActividad = () => claveDelAmbito("actividad");

/*
 * La hora del último gesto, para el bloqueo por inactividad.
 *
 * Vive aquí y no dentro del componente porque tiene que sobrevivir a recargar
 * la página: si solo estuviera en memoria, un F5 delante de la pantalla
 * bloqueada la abriría sin pedir nada, que es justo lo que el bloqueo evita.
 * Y hay que limpiarla al entrar y al salir, que es donde empieza y acaba una
 * sesión.
 */
export function leerActividad(): number {
  return Number(localStorage.getItem(claveActividad())) || 0;
}

export function apuntarActividad(cuando: number = Date.now()): void {
  localStorage.setItem(claveActividad(), String(cuando));
}

export function leerToken(): string | null {
  return localStorage.getItem(claveToken());
}

export function guardarToken(token: string): void {
  localStorage.setItem(claveToken(), token);
  // Sesión nueva, reloj nuevo: si no, quien acaba de entrar se encontraría la
  // pantalla bloqueada por la inactividad de la sesión anterior.
  localStorage.removeItem(claveActividad());
}

/*
 * Quien quiere enterarse de que la sesion se ha caido.
 *
 * Vive fuera de React, como el estado de la conexion, porque quien lo descubre
 * es `api()`, que no es un componente. Lo escucha el proveedor de sesion para
 * devolver la pantalla a la entrada.
 */
const oyentesDeLaSesion = new Set<() => void>();

/** Avisa cuando el token deja de valer. Devuelve con que darse de baja. */
export function alCaerLaSesion(oyente: () => void): () => void {
  oyentesDeLaSesion.add(oyente);
  return () => {
    oyentesDeLaSesion.delete(oyente);
  };
}

export function borrarToken(): void {
  const habia = localStorage.getItem(claveToken()) !== null;
  localStorage.removeItem(claveToken());
  localStorage.removeItem(claveActividad());
  // Solo se avisa si de verdad habia sesion: si no, cada 401 de una pantalla
  // ya cerrada volveria a disparar el aviso.
  if (habia) for (const oyente of oyentesDeLaSesion) oyente();
}

export class ErrorApi extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detalles?: unknown,
    /**
     * La operación puede haberse guardado o no, y no hay forma de saberlo.
     *
     * Pasa cuando se corta la red o el servidor deja de contestar después de
     * haber recibido la petición. Importa mucho en el mostrador: repetir una
     * entrega que sí se guardó cobra dos veces y descuenta el stock dos veces.
     */
    public readonly ambiguo = false,
    /**
     * La clave con la que esta pantalla lo dice en su idioma, y los datos de sus
     * huecos. Los manda el servidor cuando sabe decirlo de las dos formas; si no
     * vienen, se enseña su frase en castellano, que es mejor que un «algo ha ido
     * mal»: el socio al menos se la puede leer por telefono a alguien del club.
     */
    public readonly clave?: string,
    public readonly datos?: Record<string, string | number>,
  ) {
    super(message);
    this.name = "ErrorApi";
  }
}

/** Si en este tiempo el servidor no ha contestado, se deja de esperar. */
const ESPERA_MAXIMA_MS = 30_000;

/**
 * Corta la espera pasado un tiempo, para que un servidor que no contesta no
 * deje el botón en «Registrando...» para siempre.
 */
function limiteDeEspera(ms: number = ESPERA_MAXIMA_MS): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") return undefined;
  return AbortSignal.timeout(ms);
}

/**
 * Traduce un fallo de red al idioma y al problema real de quien lo lee.
 *
 * Antes se enseñaba tal cual lo que soltaba el navegador —«Failed to fetch»—,
 * en inglés y sin decir qué hacer. Y en algo que escribe hay que decir la
 * verdad incómoda: puede que sí se haya guardado.
 */
function errorDeRed(escribe: boolean): ErrorApi {
  if (escribe) {
    return new ErrorApi(
      0,
      "Se ha perdido la conexión y no se sabe si llegó a guardarse. " +
        "Compruébalo antes de repetirlo: si ya se guardó, repetirlo lo duplicaría.",
      undefined,
      true,
    );
  }
  return new ErrorApi(0, "No hay conexión con el servidor. Comprueba la red e inténtalo otra vez.");
}

type Opciones = {
  metodo?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  cuerpo?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
};

export async function api<T = unknown>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = "GET", cuerpo, query } = opciones;

  const url = new URL(`/api${ruta}`, window.location.origin);
  for (const [clave, valor] of Object.entries(query ?? {})) {
    if (valor !== undefined && valor !== null && valor !== "") url.searchParams.set(clave, String(valor));
  }

  const token = leerToken();
  const escribe = metodo !== "GET";

  let respuesta: Response;
  try {
    respuesta = await fetch(url, {
      method: metodo,
      credentials: "include",
      signal: limiteDeEspera(),
      headers: {
        ...(cuerpo !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    });
  } catch {
    // Aqui no hay respuesta: ni se sabe si el servidor llego a recibirla.
    conexionCaida();
    throw errorDeRed(escribe);
  }
  /*
   * Un 502, 503 o 504 no lo da el programa: lo da lo que hay delante cuando
   * el servidor no esta. Para el mostrador es lo mismo que no tener red -y
   * pasa en cada despliegue, medio minuto-, asi que cuenta como caida y la
   * tablet sigue con lo suyo. Cualquier otra respuesta es que hay red.
   */
  if ([502, 503, 504].includes(respuesta.status)) conexionCaida();
  else conexionViva();

  if (respuesta.status === 204) return undefined as T;

  const datos = await respuesta.json().catch(() => null);

  if (!respuesta.ok) {
    if (respuesta.status === 401) borrarToken();

    /*
     * 502, 503 y 504 no los da el programa: los da lo que hay delante cuando el
     * servidor se esta reiniciando o no contesta. En una peticion que escribe,
     * eso es igual de ambiguo que perder la red: pudo entrar y morir al
     * contestar.
     */
    if (escribe && [502, 503, 504].includes(respuesta.status)) throw errorDeRed(true);

    const mensaje = datos?.error ?? `Error ${respuesta.status}`;
    const detalles = datos?.detalles;
    // Los errores de validación llegan como lista de campos: los juntamos.
    if (Array.isArray(detalles) && detalles.length > 0 && detalles[0]?.mensaje) {
      throw new ErrorApi(
        respuesta.status,
        detalles.map((d: { campo: string; mensaje: string }) => d.mensaje).join(". "),
        detalles,
      );
    }
    throw new ErrorApi(respuesta.status, mensaje, detalles, false, datos?.clave, datos?.datos);
  }

  return datos as T;
}

/** Subida de ficheros (foto del socio). */
/**
 * Sube un fichero a la API.
 *
 * Acepta tambien un Blob suelto, que es lo que devuelve un lienzo al hacer una
 * foto con la camara o al recoger una firma: sin nombre, el multipart lo
 * tomaria por un campo de texto y el servidor no veria ningun fichero.
 */
export async function subirArchivo<T = unknown>(
  ruta: string,
  archivo: File | Blob,
  /** Para lo que tarda de verdad: restaurar una copia grande son minutos, no
      los treinta segundos que le sobran a una foto. */
  esperaMs?: number,
): Promise<T> {
  const formulario = new FormData();
  const nombre =
    archivo instanceof File ? archivo.name : `imagen.${(archivo.type.split("/")[1] ?? "png").replace("jpeg", "jpg")}`;
  formulario.append("archivo", archivo, nombre);

  const token = leerToken();
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      method: "POST",
      credentials: "include",
      signal: limiteDeEspera(esperaMs),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formulario,
    });
  } catch {
    throw errorDeRed(true);
  }

  const datos = await respuesta.json().catch(() => null);
  if (!respuesta.ok) throw new ErrorApi(respuesta.status, datos?.error ?? "No se pudo subir el archivo");
  return datos as T;
}

/**
 * Descarga un fichero de la API con la sesión puesta. Un enlace normal no
 * llevaría la cabecera de autorización, así que se baja con fetch y se entrega
 * al navegador como fichero.
 */
export async function descargar(ruta: string, nombreArchivo: string): Promise<void> {
  const token = leerToken();
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    // Descargar solo lee: si falla, no hay nada a medias que comprobar.
    throw errorDeRed(false);
  }

  if (!respuesta.ok) {
    const datos = await respuesta.json().catch(() => null);
    throw new ErrorApi(respuesta.status, datos?.error ?? "No se pudo descargar el fichero");
  }

  const url = URL.createObjectURL(await respuesta.blob());
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
